import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { FormDefinition } from "@colab/shared";
import type { AdminFormRow } from "./types";

export type UpdateFormDefinitionInput = {
  id: string;
  definition: FormDefinition;
};

type Context = {
  previousDetail: AdminFormRow | undefined;
};

// PATCH /api/admin/forms/:id/definition
//
// Whole-blob definition save. The per-field hooks (use-add-field /
// use-update-field / use-remove-field / use-reorder-fields) handle granular
// optimistic mutations; this hook is the editor's explicit "Save" path or a
// fallback for bulk edits.
//
// Pattern C state-flip on the detail cache; list cache is untouched because
// the list endpoint returns name/status only, not the definition.
export function useUpdateFormDefinition() {
  const queryClient = useQueryClient();
  return useMutation<
    { ok: true },
    ApiClientError,
    UpdateFormDefinitionInput,
    Context
  >({
    mutationFn: ({ id, definition }) =>
      apiFetch<{ ok: true }>(`/admin/forms/${id}/definition`, {
        method: "PATCH",
        body: JSON.stringify(definition),
      }),
    onMutate: async ({ id, definition }) => {
      const detailKey = queryKeys.adminForms.detail(id);
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previousDetail = queryClient.getQueryData<AdminFormRow>(detailKey);
      queryClient.setQueryData<AdminFormRow>(detailKey, (old) =>
        old ? { ...old, definition } : old,
      );
      return { previousDetail };
    },
    onError: (err, { id }, context) => {
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(queryKeys.adminForms.detail(id), context.previousDetail);
      }
      toast.error(err.message ?? "Failed to save form");
    },
    onSettled: (_data, _err, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminForms.detail(id) });
    },
  });
}
